/* Tippy Type product page — per-option variant picking, ombre bg, gallery, dialog, model gating */
class TippyProduct extends HTMLElement {
  constructor() {
    super();
    try {
      const dataEl = this.querySelector('[data-product-variants]') || document.querySelector('[data-product-variants]');
      this.variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      this.variants = [];
    }
    this.url = this.dataset.url;
    this.mainImage = this.querySelector('[data-main-image]');
    this.priceEl = this.querySelector('[data-price]');
    this.compareEl = this.querySelector('[data-compare]');
    this.descEl = this.querySelector('[data-variant-desc]');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.preorderInput = this.querySelector('[data-preorder-input]');
    this.preorderText = this.querySelector('[data-preorder-text]');
    this.atc = this.querySelector('[data-atc]');
    this.atcText = this.querySelector('[data-atc-text]');
    this.modelSelect = this.querySelector('[data-model-select]');
  }

  connectedCallback() {
    // ombre background from current variant
    this.applyOmbre(this.dataset.ombre);

    // filter gallery to current variant
    const initial = this.currentVariant();
    if (initial) this.filterThumbs(String(initial.id));

    // option pickers (color circles + option buttons)
    this.selectedOptions = initial && initial.options ? initial.options.slice() : [];
    this.querySelectorAll('[data-option-value]').forEach((btn) => {
      btn.addEventListener('click', () => this.selectOption(Number(btn.dataset.optionIndex), btn.dataset.value));
    });
    this.updateOptionUI();

    // thumbnails
    this.querySelectorAll('[data-thumb]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (this.mainImage) this.mainImage.src = btn.dataset.full;
        this.querySelectorAll('[data-thumb]').forEach((t) => t.classList.remove('is-active'));
        btn.classList.add('is-active');
      });
    });

    // device-compat dialog
    const opener = this.querySelector('[data-dialog-open]');
    if (opener) {
      const dialog = this.querySelector('#' + opener.dataset.dialogOpen);
      if (dialog) {
        opener.addEventListener('click', () => dialog.showModal());
        dialog.addEventListener('click', (e) => {
          if (e.target === dialog) dialog.close();
        });
      }
    }

    // model gating
    if (this.modelSelect) {
      this.updateModelGate();
      this.modelSelect.addEventListener('change', () => this.updateModelGate());
    }
  }

  updateModelGate() {
    if (!this.atc || !this.modelSelect) return;
    const variant = this.currentVariant();
    const soldOut = variant && !variant.available;
    this.atc.disabled = soldOut || !this.modelSelect.value;
  }

  currentVariant() {
    const id = this.variantInput ? String(this.variantInput.value) : null;
    return this.variants.find((v) => String(v.id) === id);
  }

  selectVariant(id) {
    const variant = this.variants.find((v) => String(v.id) === String(id));
    if (!variant) return;

    if (this.variantInput) this.variantInput.value = variant.id;

    // filter gallery thumbnails to this variant
    this.filterThumbs(String(variant.id));

    // main image
    if (this.mainImage && variant.image) this.mainImage.src = variant.image;

    // price
    if (this.priceEl) this.priceEl.textContent = variant.price_formatted;
    if (this.compareEl) {
      if (variant.has_compare) {
        this.compareEl.textContent = variant.compare_at_formatted;
        this.compareEl.hidden = false;
      } else {
        this.compareEl.hidden = true;
      }
    }

    // description
    if (this.descEl) this.descEl.innerHTML = variant.description || '';

    // preorder
    if (this.preorderInput && this.preorderText) {
      if (variant.preorder_text) {
        this.preorderInput.value = variant.preorder_text;
        this.preorderInput.disabled = false;
        this.preorderText.textContent = variant.preorder_text;
        this.preorderText.hidden = false;
      } else {
        this.preorderInput.value = '';
        this.preorderInput.disabled = true;
        this.preorderText.hidden = true;
      }
    }

    // ombre
    this.applyOmbre(variant.color);

    // sync option picker state (a fallback match may change other options too)
    if (variant.options) this.selectedOptions = variant.options.slice();
    this.updateOptionUI();

    // ATC state
    if (this.atc && this.atcText) {
      if (!variant.available) {
        this.atc.disabled = true;
        this.atcText.textContent = 'SOLD OUT';
      } else {
        this.atcText.textContent = 'ADD TO CART';
        this.atc.disabled = false;
      }
    }
    if (this.modelSelect) this.updateModelGate();

    // URL (no reload)
    if (variant.url) {
      window.history.replaceState({}, '', variant.url);
    }
  }

  selectOption(index, value) {
    const variant = this.resolveVariant(index, value);
    if (!variant) return;
    this.selectVariant(variant.id);
  }

  /* Best variant for setting option[index] = value while keeping the other
     selected options where possible. Exact match first; otherwise prefer
     variants matching the most other options, breaking ties on availability. */
  resolveVariant(index, value) {
    const want = this.selectedOptions.slice();
    want[index] = value;
    const exact = this.variants.find(
      (v) => v.options && v.options.length === want.length && v.options.every((o, i) => o === want[i])
    );
    if (exact) return exact;

    let best = null;
    let bestScore = -1;
    this.variants.forEach((v) => {
      if (!v.options || v.options[index] !== value) return;
      const matches = v.options.reduce((s, o, i) => s + (i !== index && o === want[i] ? 1 : 0), 0);
      const score = matches * 2 + (v.available ? 1 : 0);
      if (score > bestScore) {
        bestScore = score;
        best = v;
      }
    });
    return best;
  }

  updateOptionUI() {
    this.querySelectorAll('[data-option-value]').forEach((btn) => {
      const index = Number(btn.dataset.optionIndex);
      const selected = this.selectedOptions[index] === btn.dataset.value;
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      const target = this.resolveVariant(index, btn.dataset.value);
      btn.classList.toggle('is-soldout', !target || !target.available);
    });
  }

  filterThumbs(variantId) {
    const thumbs = this.querySelectorAll('[data-thumb]');
    let firstMatch = null;
    thumbs.forEach((btn) => {
      const belongs = !btn.dataset.variantId || btn.dataset.variantId === variantId;
      btn.style.display = belongs ? '' : 'none';
      if (belongs && !firstMatch) firstMatch = btn;
    });
    if (firstMatch) {
      firstMatch.click();
    }
  }

  applyOmbre(color) {
    if (!color) return;
    document.body.style.transition = 'background 600ms ease';
    document.body.style.background = `radial-gradient(ellipse at top, #fff 0%, ${color} 160%)`;
  }
}

customElements.define('tippy-product', TippyProduct);
